import { RealTimeDeviceData } from 'model';
import UnitSystem, { UnitConverter, UnitFormat } from 'model/UnitSystem';
import { SystemMode, ValveState } from 'model';
import { toMeasure } from 'model/api/Device';

interface NotificationStatsFirestore {
  infoCount: number;
  warningCount: number;
  criticalCount: number;
}

interface NotificationsFirestore {
  pending: NotificationStatsFirestore
}

type SystemModeValues = 'home' | 'away' | 'sleep';
interface SystemModeFirestore {
  lastKnown: SystemModeValues;
}

type ValveValues = 'open' | 'closed' | 'in_transition' | 'unknown';
interface DeviceValveFirestore {
  lastKnown: ValveValues;
}

interface CurrentTelemetryFirestore {
  gpm?: number;
  psi?: number;
  tempF?: number;
  updated?: string;
}
interface TelemetryFirestore {
  current: CurrentTelemetryFirestore;
}

interface DeviceFirestoreSnapshot {
  deviceId: string;
  isConnected: boolean;
  notifications?: NotificationsFirestore;
  systemMode?: SystemModeFirestore;
  valve?: DeviceValveFirestore;
  telemetry?: TelemetryFirestore;
}

const toSystemMode = (systemMode: SystemModeValues): SystemMode => {
  switch(systemMode) {
    case 'home':
      return SystemMode.HOME;
    case 'sleep':
      return SystemMode.SLEEP;
    case 'away':
      return SystemMode.AWAY;
    default:
      return SystemMode.HOME;
  }
}

const toValveState = (valveState: ValveValues): ValveState => {
  switch(valveState) {
    case 'open':
      return ValveState.OPEN;
    case 'closed':
      return ValveState.CLOSED;
    case 'in_transition':
      return ValveState.IN_TRANSITION;
    case 'unknown':
    default:
      return ValveState.UNKNOWN;
  }
}

export const toRealtimeDeviceData = (snapshot: DeviceFirestoreSnapshot, unit: UnitSystem): RealTimeDeviceData => ({
  macAddress: snapshot.deviceId,
  isConnected: snapshot.isConnected,
  notifications: {
    pending: {
      criticalCount: snapshot.notifications?.pending.criticalCount || 0,
      warningCount: snapshot.notifications?.pending.warningCount || 0,
      infoCount: snapshot.notifications?.pending.infoCount || 0,
    }
  },
  systemMode: {
    lastKnown: toSystemMode(snapshot.systemMode?.lastKnown || 'home'),
  },
  valve: {
    lastKnown: toValveState(snapshot.valve?.lastKnown || 'closed'),
  },
  telemetry: {
    updated: snapshot.telemetry?.current.updated || '2000-01-01T00:00:00Z',
    temp: toMeasure(unit, snapshot.telemetry?.current.tempF, UnitConverter.convertFromFahrenheit, UnitFormat.displayLongTempUnit, UnitFormat.displayShortTempUnit),
    flow: toMeasure(unit, snapshot.telemetry?.current.gpm, UnitConverter.convertFromGallons, UnitFormat.displayLongFlowUnit, UnitFormat.displayShortFlowUnit),
    pressure: toMeasure(unit, snapshot.telemetry?.current.psi, UnitConverter.convertFromPSI, UnitFormat.displayLongPressureUnit, UnitFormat.displayShortPressureUnit),
    battery: {
      value: 0,
      long: 'Battery',
      short: '%',
      unit
    },
    humidity: {
      value: 0,
      long: 'Humidity',
      short: '%',
      unit
    }
  }
})